/**
 * БДР v2 (ТЗ AI-Economist-2026 ред. 2.1, п. 6.6): период «месяц × объект»,
 * собранный из первичных источников. Контракт — `/agents/econ/bdr`
 * (`osnova-api/src/routes/econ_bdr.rs`). Деньги приходят строкой с копейками
 * («1234.50») — точные, складывает бэкенд.
 */

export type BdrStatus = 'draft' | 'preliminary' | 'submitted' | 'approved' | 'superseded' | string;

export interface BdrPermissions {
  read: boolean;
  write: boolean;
  approve: boolean;
  payroll_read: boolean;
  cash_assign: boolean;
}

export interface BdrStatusInfo {
  enabled: boolean;
  flag: string;
  chronum_configured: boolean;
  permissions: BdrPermissions;
  statuses: { code: string; title: string }[];
}

export interface BdrPeriodHeader {
  id: string;
  period: string;
  period_start: string;
  scope_kind: string;
  scope_key: string;
  scope_label: string;
  version: number;
  is_current: boolean;
  status: BdrStatus;
  status_title: string;
  collect_state: 'idle' | 'queued' | 'running' | 'failed' | string;
  collect_error: string | null;
  collected_at: string | null;
  submitted_by_name: string | null;
  submitted_at: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  reopen_reason: string | null;
  summary?: Record<string, unknown>;
}

export interface BdrGridItem extends BdrPeriodHeader {
  revenue: string | null;
  costs: string | null;
  margin: string | null;
  no_data_lines: number;
  open_critical: number;
}

export interface BdrArticle {
  code: string;
  name: string;
  parent_code: string | null;
  kind: 'revenue' | 'direct_cost' | 'distribution' | 'total' | 'info' | string;
  template_row: number | null;
  primary_source: string;
  unit: 'rub' | 'people' | 'pct' | string;
  editable: boolean;
  counts_in_parent: boolean;
}

export interface BdrColumn {
  key: string;
  kind: 'project' | 'cost_center' | string;
  project_id: string | null;
  cost_center_id: string | null;
  label: string;
}

export interface BdrLine {
  id: string;
  article_code: string;
  column_key: string;
  source: string;
  source_amount: string | null;
  manual_amount: string | null;
  manual_reason: string | null;
  manual_by_name: string | null;
  manual_at: string | null;
  amount: string | null;
  plan_amount: string | null;
  plan_delta: string | null;
  plan_delta_pct: string | null;
  plan_significant: boolean;
  no_data: boolean;
  hint: string | null;
  updated_at: string;
}

export interface BdrAnomaly {
  id: string;
  kind: string;
  severity: 'critical' | 'major' | 'minor' | string;
  title: string;
  description: string;
  column_key: string | null;
  article_code: string | null;
  amount: string | null;
  details: Record<string, unknown>;
  decision: 'accepted' | 'rejected' | null;
  decision_comment: string | null;
  decision_by_name: string | null;
  decision_at: string | null;
}

export interface BdrChecks {
  can_collect: boolean;
  collect_blocked_reason: string | null;
  can_edit: boolean;
  can_submit: boolean;
  submit_blocked_reason: string | null;
  can_approve: boolean;
  approve_blocked_reason: string | null;
  can_reopen: boolean;
  reopen_blocked_reason: string | null;
  no_data_lines: number;
  unresolved_critical: number;
  chronum_period_open: boolean;
}

/** Приход ТМЦ изменился после сборки: строка материалов, было → стало. */
export interface BdrDrift {
  article: string;
  column_key: string;
  column_label: string | null;
  stored: string;
  current: string;
  delta: string;
}

export interface BdrPeriodView {
  period: BdrPeriodHeader;
  columns: BdrColumn[];
  articles: BdrArticle[];
  lines: BdrLine[];
  totals: Record<string, string | null>;
  anomalies: BdrAnomaly[];
  anomaly_counts: { critical: number; major: number; minor: number; open_critical: number };
  details_hidden: boolean;
  checks: BdrChecks;
  permissions: BdrPermissions;
  versions: BdrPeriodHeader[];
  materials_drift?: BdrDrift[];
}

export const BDR_SOURCE_LABEL: Readonly<Record<string, string>> = {
  ks: 'КС и пакеты КС',
  mining: 'Добыча (М2)',
  canteen: 'Столовая (М3)',
  cash: 'Банк («Деньги»)',
  chronum_payroll: 'Хронум: начислено',
  payroll_pct: '% от ФОТ',
  chronum_machine: 'Хронум: маш.-часы × ставка',
  chronum_headcount: 'Хронум: численность',
  calc: 'Расчёт',
  debt_schedule: 'Графики кредитов',
  manual: 'Вручную',
  none: '—',
  mto_receipts: 'Приход ТМЦ',
};

export const BDR_SEVERITY_LABEL: Readonly<Record<string, string>> = {
  critical: 'критично',
  major: 'важно',
  minor: 'к сведению',
};

/** Статья со своей глубиной в дереве шаблона. */
export interface BdrRow {
  article: BdrArticle;
  depth: number;
  hasChildren: boolean;
}

/**
 * Статьи в порядке шаблона «БДР сводная» деревом: родитель, затем его
 * дети. Порядок — как пришёл с бэкенда (он уже по шаблону).
 */
export function bdrRows(articles: readonly BdrArticle[]): BdrRow[] {
  const byParent = new Map<string | null, BdrArticle[]>();
  const known = new Set(articles.map((a) => a.code));
  for (const a of articles) {
    const p = a.parent_code && known.has(a.parent_code) ? a.parent_code : null;
    const list = byParent.get(p) ?? [];
    list.push(a);
    byParent.set(p, list);
  }
  const out: BdrRow[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const a of byParent.get(parent) ?? []) {
      const children = byParent.get(a.code) ?? [];
      out.push({ article: a, depth, hasChildren: children.length > 0 });
      walk(a.code, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

const RUB0 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const RUB2 = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Значение строки по единице: ₽ без копеек, люди, проценты. */
export function formatBdrValue(v: string | null | undefined, unit: string): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  if (unit === 'pct') return `${n.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`;
  if (unit === 'people') return n.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  return RUB0.format(n);
}

export function formatRub2(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? RUB2.format(n) : v;
}

/** Текущий месяц «ГГГГ-ММ». */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
